/**
 * Every harness the desktop shell can drive, ready or not (RFC 0337 S6).
 *
 * `useLocalHarnesses` is the ready ones, for the composer. The first run needs
 * the rest too: installed but logged out, or not installed, each with the one
 * command that fixes it. The shell re-probes every ten seconds, so a login done
 * in a terminal shows up here without a reload.
 *
 * Outside the desktop shell nothing publishes this, and the hook returns null,
 * which the caller reads as "cannot tell" rather than "nothing installed".
 */

import { useEffect, useState } from 'react'

export const HARNESS_STATUSES_GLOBAL = '__modHarnessStatuses'
export const HARNESS_STATUSES_EVENT = 'mod:harness-statuses'

export interface HarnessStatusRow {
  id: string
  label: string
  /** connected | unauthenticated | expired | notOnPath | notInstalled | unsupported */
  state: string
  version?: string
  plan?: string
  /** The command or step that makes it ready, when it is not. */
  remedy?: string
}

function readGlobal(): HarnessStatusRow[] | null {
  try {
    const raw = (globalThis as unknown as Record<string, unknown>)[
      HARNESS_STATUSES_GLOBAL
    ]
    return Array.isArray(raw) ? (raw as HarnessStatusRow[]) : null
  } catch {
    return null
  }
}

export function useHarnessStatuses(): HarnessStatusRow[] | null {
  const [rows, setRows] = useState<HarnessStatusRow[] | null>(readGlobal)
  useEffect(() => {
    const onPublish = (e: Event) => {
      const detail = (e as CustomEvent<unknown>).detail
      setRows(
        Array.isArray(detail) ? (detail as HarnessStatusRow[]) : readGlobal(),
      )
    }
    window.addEventListener(HARNESS_STATUSES_EVENT, onPublish)
    setRows(readGlobal())
    return () => window.removeEventListener(HARNESS_STATUSES_EVENT, onPublish)
  }, [])
  return rows
}
